import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryRetailService from '../../../services/nurseryRetail';
import { errorMessage } from '../../../utils/apiResponse';
import type { XdockSettings } from '../../../types/nurseryRetail';

/** 480 → '08:00', for a time input. */
const toHHMM = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** '08:00' → 480; null when it is not a time. */
function fromHHMM(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  return minutes >= 0 && minutes < 24 * 60 ? minutes : null;
}

/**
 * The same-day cut-off: a purchase order imported before it can be made up
 * today; one imported after it goes to the store's next pick day. The server
 * applies it, in the organisation's time zone.
 */
export function XdockCutoffSetting() {
  const { t } = useTranslation('nursery', { keyPrefix: 'XdockCutoffSetting' });
  const [settings, setSettings] = useState<XdockSettings | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    nurseryRetailService.getSettings()
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) {
          setSettings(res.data);
          setValue(toHHMM(res.data.same_day_cutoff_minutes));
        } else setError(errorMessage(res, t('readFailed')));
      })
      .catch((e) => { if (live) setError(errorMessage(e, t('readFailed'))); });
    return () => { live = false; };
  }, [t]);

  const minutes = fromHHMM(value);
  const changed = settings != null && minutes != null && minutes !== settings.same_day_cutoff_minutes;

  const save = async () => {
    if (minutes == null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const res = await nurseryRetailService.saveSettings(minutes);
      if (res.success && res.data) {
        setSettings(res.data);
        setValue(toHHMM(res.data.same_day_cutoff_minutes));
        setSaved(true);
      } else setError(errorMessage(res, t('saveFailed')));
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label={t('title')} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Clock className="h-4 w-4 mt-0.5 text-neutral-500 flex-shrink-0" />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-neutral-800">{t('title')}</h2>
          <p className="text-sm text-neutral-600">
            {t('description')}
          </p>
        </div>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      {settings && (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="xdock-cutoff" className="block text-sm font-medium text-neutral-700 mb-1">{t('timeLabel')}</label>
            <input
              id="xdock-cutoff"
              type="time"
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={value}
              onChange={(e) => { setValue(e.target.value); setSaved(false); }}
            />
          </div>
          <Button size="sm" onClick={save} disabled={busy || !changed}>{busy ? t('saving') : t('save')}</Button>
          {saved && <span className="text-sm text-green-700">{t('saved', { label: settings.cutoff_label })}</span>}
          {!saved && settings.is_default && <span className="text-xs text-neutral-500">{t('default')}</span>}
        </div>
      )}
    </section>
  );
}
